import { useFormik } from "formik";
import { useNavigate } from "react-router";
import { useConfirm } from "../context/ConfirmContext";

/**
 * Reusable Form Hook
 * @param {Object} initialValues - Initial form values
 * @param {Object} validationSchema - Yup validation schema
 * @param {Function} apiCall - API function to call
 * @param {Function} onSuccess - Callback after success (receives API response)
 * @param {Function} onError - Callback after error (receives error message)
 * @param {string|number|null} userId - If provided, we call API with userId
 * @param {Object} options - { confirm: false } skips the save confirmation
 *   (login/register); every add/edit form asks before saving by default.
 * @returns Formik instance
 */
export const useFormHandler = (
  initialValues,
  validationSchema,
  apiCall,
  onSuccess,
  onError,
  idPk = null,
  { confirm: askConfirm = true } = {}
) => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  return useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        // Runs after validation passed, right before anything is sent.
        if (askConfirm) {
          const ok = await confirm(
            idPk
              ? { title: "Confirm Update", content: "Save the changes to this data?", confirmLabel: "Save" }
              : { title: "Confirm Add", content: "Add this new data?", confirmLabel: "Add" }
          );
          if (!ok) return;
        }

        // Validate apiCall is a function
        if (typeof apiCall !== 'function') {
          throw new Error('API function is not defined');
        }

        let response;
        if (idPk) {
          // Jika userId ada => update
          // Pastikan apiCall di sisi luar siap menerima (userId, values)
          response = await apiCall(idPk, values);
        } else {
          // Jika userId tidak ada => create/register
          // Pastikan apiCall siap menerima (values) saja
          response = await apiCall(values);
        }

        // Callback sukses
        if (onSuccess) onSuccess(response);

      } catch (error) {
        console.error("❌ API Error:", error);

        let errorMessage = "An error occurred. Please try again."; // Default error message


        if(error.message){
            errorMessage = error.message;
        }

        if (error.response) {
          // Jika API mengembalikan pesan error
          errorMessage = error.response.data?.message || errorMessage;

          if (error.response.data?.errors) {
            // Gabungkan semua pesan error ke dalam satu string
            errorMessage = Object.values(error.response.data.errors)
              .flat()
              .join(" ");
          }
        }

        setErrors({ apiError: errorMessage });

        // Callback error
        if (onError) onError(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });
};
